/**
 * AdminFbAdsTab — FB Ads Library.
 *
 * For each product (revenue_leak, ads_preview, ai_intelligence, brand_monitor,
 * ads_creator, blog_agent, seo_autopilot), shows 3 ready-to-ship FB ad
 * creatives: primary_text, headline, description, CTA. Each creative has a
 * one-click "copy-all" button that dumps a Meta Ads Manager-ready text block.
 *
 * Admin can also trigger an AI re-generation per product (with an optional
 * angle hint) and delete/edit existing creatives inline.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Megaphone, Copy, Sparkles, Trash2, Check, Loader2, ExternalLink,
  RefreshCw, Languages,
} from "lucide-react";

const CTA_LABELS = {
  SHOP_NOW: "Kúpiť teraz",
  LEARN_MORE: "Zistiť viac",
  SIGN_UP: "Zaregistrovať sa",
  SUBSCRIBE: "Odoberať",
  GET_OFFER: "Získať ponuku",
};

export default function AdminFbAdsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [lang, setLang] = useState("sk");
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    const r = await axios.get(`${API}/admin/fb-ads`, { params: { language: lang } });
    setProducts(r.data.products || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [lang]);  // eslint-disable-line

  const seed = async () => {
    await axios.post(`${API}/admin/fb-ads/seed`);
    await load();
  };

  const generate = async (p) => {
    setGenerating(p.product_id);
    await axios.post(`${API}/admin/fb-ads/generate`, {
      product_id: p.product_id, product_name: p.product_name,
      landing_url: p.landing_url, price: p.price, language: lang,
    }).catch(() => null);
    setGenerating(null);
    await load();
  };

  const remove = async (ad_id) => {
    if (!window.confirm("Vymazať túto reklamu?")) return;
    await axios.delete(`${API}/admin/fb-ads/${ad_id}`);
    await load();
  };

  const copyAd = (ad, product) => {
    const text =
`=== FB Ad · ${product.product_name} (${product.price}) · ${ad.angle} ===

PRIMARY TEXT:
${ad.primary_text}

HEADLINE: ${ad.headline}
DESCRIPTION: ${ad.description}
CTA: ${ad.cta}
DESTINATION URL: ${window.location.origin}${product.landing_url}`;
    // Primary path (works in normal browsers) — fall back to a hidden textarea
    // for environments where Clipboard API is blocked (iframes, headless tests).
    const finish = () => { setCopied(ad.id); setTimeout(() => setCopied(null), 2000); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(finish).catch(() => legacyCopy(text, finish));
    } else {
      legacyCopy(text, finish);
    }
  };

  const legacyCopy = (text, done) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      document.execCommand("copy");
      ta.remove();
      done();
    } catch { /* ignore — user can retry */ }
  };

  return (
    <div className="space-y-6" data-testid="fb-ads-tab">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-purple-600" /> FB Ads Library
          </h2>
          <p className="text-sm text-gray-500">Ready-to-ship reklamné texty pre každú podstránku — stačí copy & paste do Meta Ads Managera.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-md p-0.5" data-testid="fb-ads-lang">
            {["sk", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)}
                className={`px-3 py-1 text-xs font-bold rounded uppercase ${lang === l ? "bg-white shadow text-gray-900" : "text-gray-500"}`}>
                <Languages className="w-3 h-3 inline mr-1" /> {l}
              </button>
            ))}
          </div>
          <button onClick={seed} data-testid="fb-ads-seed" className="px-3 py-2 border border-gray-300 text-xs font-bold rounded hover:bg-gray-50 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Seed defaults
          </button>
        </div>
      </header>

      {loading ? (
        <div className="py-16 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : products.length === 0 ? (
        <div className="bg-white border border-gray-200 p-10 text-center text-gray-500">
          <Megaphone className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-bold mb-1">Žiadne reklamné texty pre jazyk "{lang}"</p>
          <p className="text-xs">Klikni "Seed defaults" alebo "Generate with AI" pri konkrétnom produkte.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {products.map((p) => (
            <section key={p.product_id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`fb-ads-product-${p.product_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    {p.product_name}
                    <span className="px-2 py-0.5 text-xs font-mono font-bold bg-purple-100 text-purple-700 rounded">{p.price}</span>
                  </h3>
                  <a href={p.landing_url} target="_blank" rel="noreferrer" className="text-xs text-gray-500 hover:text-purple-600 inline-flex items-center gap-1">
                    {window.location.origin}{p.landing_url} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <button onClick={() => generate(p)} disabled={generating === p.product_id} data-testid={`fb-ads-gen-${p.product_id}`}
                  className="px-3 py-2 bg-purple-600 text-white text-xs font-bold rounded hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1">
                  {generating === p.product_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                  Generate with AI
                </button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {p.ads.map((ad, i) => (
                  <div key={ad.id} className="border border-gray-200 rounded p-4 bg-gray-50" data-testid={`fb-ad-${p.product_id}-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-mono uppercase font-bold text-purple-600 tracking-wider">{ad.angle}</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => copyAd(ad, p)} data-testid={`fb-ad-copy-${ad.id}`}
                          className="text-gray-400 hover:text-purple-600" title="Copy to clipboard">
                          {copied === ad.id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                        <button onClick={() => remove(ad.id)} className="text-gray-400 hover:text-red-500" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="text-xs text-gray-700 whitespace-pre-wrap mb-3 line-clamp-[8]" title={ad.primary_text}>{ad.primary_text}</div>
                    <div className="border-t border-gray-200 pt-2 space-y-1 text-xs">
                      <p><b className="text-gray-900">Headline:</b> <span className="text-gray-600">{ad.headline}</span></p>
                      <p><b className="text-gray-900">Description:</b> <span className="text-gray-600">{ad.description}</span></p>
                      <p><b className="text-gray-900">CTA:</b> <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded font-bold text-[10px]">{CTA_LABELS[ad.cta] || ad.cta}</span></p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded p-4 text-xs text-blue-900">
        💡 <b>Návod:</b> Kliknutím na <Copy className="w-3 h-3 inline mx-0.5" /> skopíruješ celú reklamu vo formáte pre Meta Ads Manager.
        V Ads Managerovi vytvor novú reklamu → Prilož text z clipboardu → Target URL sa automaticky vyplní.
      </div>
    </div>
  );
}
